import axios from 'axios';
import axiosInstance from 'utils/axiosInstance';
import { hasFeature } from 'utils/featureFlags';
import {
  OUTLET_BY_ID,
  OUTLET_NEW,
  OUTLET_PRICE,
  OUTLETS_SEARCH,
  OUTLETS_URL,
  UPDATE_SELECTED_OUTLET,
  OUTLET_BY_ID_BUSINESS_SERVICE,
  OUTLET_LIST,
  SAUDAGAR_OUTLET_LIST,
  SAUDAGAR_OUTLET_BY_ID,
} from './constants';

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L20
 * @param outletId
 * @param query
 * @return {Axios.IPromise<U>}
 */
export function getOutletById(outletId, query) {
  if (hasFeature('enable-cmp-indicators')) {
    return getSaudagarOutletById(outletId, query).then(resp => resp.data);
  }

  return axios
    .get(OUTLET_BY_ID(outletId), { params: query, withCredentials: true })
    .catch(error => {
      let eobj;
      if (error.response) {
        const { data } = error.response;
        if (data.errors) {
          eobj = new Error(data.errors);
        } else if (data.message) {
          eobj = new Error(data.message);
        } else {
          eobj = new Error(error.message);
        }
      } else {
        eobj = new Error(error.message);
      }
      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L26
 * @param data
 * @param params
 * @return {Axios.IPromise<U>}
 */
export function postOutlet(data, params) {
  return axios
    .post(OUTLETS_URL, data, { params, withCredentials: true })
    .catch(error => {
      let eobj;
      if (error.response) {
        const {
          response: { data },
        } = error;
        if (data.errors) {
          eobj = new Error(data.errors);
        } else if (data.message) {
          eobj = new Error(data.message);
        } else {
          eobj = new Error(error.message);
        }
      } else {
        eobj = new Error(error.message);
      }
      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L68
 * @param outletId
 * @param data
 * @param queryParams
 * @return {Axios.IPromise<U>}
 */
export function putOutletById(outletId, data, queryParams) {
  return axios
    .put(OUTLET_BY_ID(outletId), data, {
      params: queryParams,
      withCredentials: true,
    })
    .catch(error => {
      if (error.response) {
        const eobj = new Error(error.response.data.errors);
        return Promise.reject(eobj);
      }
      return Promise.reject(new Error(error.message));
    });
}

/**
 * TODO add link
 * @param outletId
 * @param data
 * @param queryParams
 * @return {Axios.IPromise<U>}
 */
export function patchOutletById(outletId, data, queryParams) {
  return axios
    .patch(OUTLET_BY_ID(outletId), data, {
      params: queryParams,
      withCredentials: true,
    })
    .catch(error => {
      let eobj;
      if (error.response) {
        const {
          response: { data },
        } = error;
        if (data.errors) {
          eobj = new Error(data.errors);
        } else if (data.message) {
          eobj = new Error(data.message);
        } else {
          eobj = new Error(error.message);
        }
      } else {
        eobj = new Error(error.message);
      }
      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L8
 * @return {Axios.IPromise<U>}
 */
export function outlets(params) {
  if (hasFeature('enable-cmp-indicators')) {
    if (params?.with_inactive) {
      params.active = 'all';
    }
    delete params?.with_inactive;

    // v4 outlets
    return getSaudagarOutlets(params).then(resp => resp.data);
  }

  return axios
    .get(OUTLETS_URL, { params, withCredentials: true })
    .catch(error => {
      let eobj;
      if (error.response) {
        const { data } = error.response;
        if (data.errors) {
          eobj = new Error(data.errors);
        } else if (data.message) {
          eobj = new Error(data.message);
        } else {
          eobj = new Error(error.message);
        }
      } else {
        eobj = new Error(error.message);
      }
      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L113
 * @param params
 * @return {Axios.IPromise<U>}
 */
export function search(params) {
  return axios
    .get(OUTLETS_SEARCH, { params, withCredentials: true })
    .catch(error => {
      if (error.response) {
        const {
          data: { errors, message },
        } = error.response;
        if (errors) {
          return new Error(errors);
        }
        return new Error(message);
      }
      const eobj = new Error(error.message);

      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v1/outlets_controller.rb#L124-L124
 * @param params
 * @return {Axios.IPromise<U>}
 */
export function getNew(params) {
  return axios
    .get(OUTLET_NEW, { params, withCredentials: true })
    .catch(error => {
      if (error.response) {
        const {
          data: { errors, message },
        } = error.response;
        if (errors) {
          return new Error(errors);
        }
        return new Error(message);
      }
      const eobj = new Error(error.message);

      return Promise.reject(eobj);
    });
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v2/sessions_controller.rb#L65
 * @param data
 * @return {Axios.IPromise<U>}
 */
export function patchLogin(data, params) {
  return axios
    .patch(UPDATE_SELECTED_OUTLET, data, { params, withCredentials: true })
    .then(() => true)
    .catch(error => {
      if (error.response && error.response.data.error) {
        return Promise.reject(new Error(error.response.data.error));
      }
      return Promise.reject(error);
    });
}

export function getOutletPrice() {
  return axios.get(OUTLET_PRICE, { withCredentials: true }).catch(error => {
    if (error.response && error.response.data.error) {
      return Promise.reject(new Error(error.response.data.error));
    }
    return Promise.reject(error);
  });
}

/**
 * Used only by receipt page.
 *
 * @param outletId
 * @param query
 * @return {Axios.IPromise<U>}
 * @desc get outlet by id
 */
export function getOutletBusinessService(outletId, query) {
  return axios
    .get(OUTLET_BY_ID_BUSINESS_SERVICE(outletId), {
      params: query,
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * @param outletId
 * @param data
 * @param queryParams
 * @return {Axios.IPromise<U>}
 * @desc update outlet by id
 */
export function updateOutlet(outletId, data, queryParams) {
  return axios
    .put(OUTLET_BY_ID_BUSINESS_SERVICE(outletId), data, {
      params: queryParams,
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 * @param data
 * @param queryParams
 * @return {Axios.IPromise<U>}
 * @desc create outlet
 */
export function createOutlet(data, queryParams) {
  return axios
    .post(OUTLET_LIST, data, {
      params: queryParams,
    })
    .catch(error => {
      return Promise.reject(new Error(error.message));
    });
}

/**
 *
 * @return {Axios.IPromise<U>}
 */
export function getOutlets(params) {
  if (hasFeature('enable-cmp-indicators')) {
    if (params?.with_inactive) {
      params.active = 'all';
    }
    delete params?.with_inactive;

    // v4 outlets
    return getSaudagarOutlets(params);
  }

  return axios
    .get(OUTLET_LIST, {
      params,
    })
    .catch(error => {
      if (error.response) {
        const eobj = new Error(error.response.data.errors);
        return Promise.reject(eobj);
      }
      return Promise.reject(new Error(error.message));
    });
}

export function getSaudagarOutlets(params) {
  return axiosInstance
    .get(SAUDAGAR_OUTLET_LIST, {
      params: {
        ...params,
        // To workaround the default paging of 10 items. Ideally we want to do paging
        size: 99999,
      },
      transformResponse: data => {
        try {
          const parsed = JSON.parse(data);
          parsed.data.sort((a, b) => a.name.localeCompare(b.name));
          return parsed;
        } catch (e) {
          return data;
        }
      },
    })
    .catch(error => {
      if (error.response) {
        const eobj = new Error(error.response.data.errors);
        return Promise.reject(eobj);
      }
      return Promise.reject(new Error(error.message));
    });
}

export function getSaudagarOutletById(id, params) {
  return axiosInstance
    .get(SAUDAGAR_OUTLET_BY_ID(id), { params })
    .catch(error => {
      if (error?.response?.data?.error?.internal_message) {
        const eobj = new Error(error.response.data.error.internal_message);
        return Promise.reject(eobj);
      }
      return Promise.reject(new Error(error.message));
    });
}
